import type { ThreadStatus } from '@atlas/api-contract';
import { Layers } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ViewerPanelFilters } from '@/features/viewer/viewer-store';

const STATUS_OPTIONS: readonly { value: ThreadStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' },
];

const isThreadStatus = (value: string): value is ThreadStatus =>
  STATUS_OPTIONS.some((option) => option.value === value);

export interface ThreadPanelFiltersProps {
  readonly filters: ViewerPanelFilters;
  readonly onChange: (patch: Partial<ViewerPanelFilters>) => void;
  /** Nothing to scope to until the agent has said which screen is up. */
  readonly screenKnown: boolean;
}

/**
 * The panel's two filters (spec R6.2).
 *
 * `open` is the default because the panel's job is the outstanding work: a
 * version that has been reviewed properly accumulates far more resolved threads
 * than open ones, and opening onto the archive would bury the four things still
 * waiting for someone.
 *
 * "Only this screen" is off by default for the opposite reason — a reviewer
 * opening the panel wants to know what is left on the whole version, not on
 * whichever screen the iframe happens to be showing. It is a *narrowing* the
 * reviewer asks for once they are working on one screen, and it narrows the
 * pins on the canvas at the same time as the list.
 */
export const ThreadPanelFilters = ({
  filters,
  onChange,
  screenKnown,
}: ThreadPanelFiltersProps) => (
  <div className="flex items-center gap-2">
    <Select
      value={filters.status}
      onValueChange={(value) => {
        if (isThreadStatus(value)) {
          onChange({ status: value });
        }
      }}
    >
      <SelectTrigger
        className="h-8 w-auto min-w-[6.5rem] gap-2 px-2.5 text-xs font-normal shadow-none focus:ring-1"
        aria-label="Filter threads by status"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="start">
        {STATUS_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value} className="text-xs">
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>

    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant={filters.currentScreenOnly ? 'secondary' : 'outline'}
          aria-pressed={filters.currentScreenOnly}
          disabled={!screenKnown}
          className="h-8 gap-1.5 px-2.5 text-xs font-normal"
          onClick={() => {
            onChange({ currentScreenOnly: !filters.currentScreenOnly });
          }}
        >
          <Layers aria-hidden="true" className="size-3.5" />
          This screen
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {screenKnown
          ? 'Show only the threads pinned to the screen on display'
          : 'Available once the prototype reports which screen it is on'}
      </TooltipContent>
    </Tooltip>
  </div>
);
